import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import {
  fetchOperationsManagementSettings,
  saveOperationsManagementSettings,
} from "../../../../services/operationsManagement";
import {
  PROOF_ENFORCEMENT_MODES,
  PROOF_ENFORCEMENT_OPTIONS,
  normalizeProofEnforcementMode,
} from "../../../../constants/proofEnforcementModes";

export const DEFAULT_PURCHASING_SETTINGS = {
  showPoUrl: false,
  showPoNotesAction: true,
  infiniteDashboardScroll: false,
  requireShippingConfirmation: false,
  proofEnforcementMode: PROOF_ENFORCEMENT_MODES.NONE,
  evaluateProofRequirementsPerPurchaseOrder: false,
};

const PurchasingSettingsPanel = ({ instanceUniqueId, canManage = false }) => {
  const [settings, setSettings] = useState(DEFAULT_PURCHASING_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canManageFoundation, setCanManageFoundation] = useState(false);
  const [error, setError] = useState("");

  const purchasingSettings = useMemo(
    () => ({
      ...DEFAULT_PURCHASING_SETTINGS,
      ...settings,
      proofEnforcementMode: normalizeProofEnforcementMode(
        settings?.proofEnforcementMode
      ),
    }),
    [settings]
  );

  const loadSettings = useCallback(async () => {
    if (!instanceUniqueId) return;

    setLoading(true);
    setError("");
    try {
      const response = await fetchOperationsManagementSettings(instanceUniqueId);
      setCanManageFoundation(response?.permissions?.canManageFoundationalSettings === true);
      setSettings({
        ...DEFAULT_PURCHASING_SETTINGS,
        ...(response?.settings?.purchasing || {}),
        proofEnforcementMode: normalizeProofEnforcementMode(
          response?.settings?.purchasing?.proofEnforcementMode
        ),
      });
    } catch (loadError) {
      setCanManageFoundation(false);
      setError(loadError?.response?.data?.error || "Unable to load Purchasing settings.");
    } finally {
      setLoading(false);
    }
  }, [instanceUniqueId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const savePurchasingSettings = async (updates) => {
    const nextSettings = { ...purchasingSettings, ...updates };
    setSaving(true);
    setError("");
    try {
      const response = await saveOperationsManagementSettings({
        instanceUniqueId,
        purchasing: nextSettings,
      });
      setSettings({
        ...DEFAULT_PURCHASING_SETTINGS,
        ...(response?.settings?.purchasing || nextSettings),
        proofEnforcementMode: normalizeProofEnforcementMode(
          response?.settings?.purchasing?.proofEnforcementMode
            ?? nextSettings.proofEnforcementMode
        ),
      });
    } catch (saveError) {
      setError(saveError?.response?.data?.error || "Unable to save Purchasing settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Stack spacing={0.5}>
        <Typography variant="h6">Purchasing Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Configure instance-specific Purchasing behaviour and workflow options.
        </Typography>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {canManage && !canManageFoundation && !loading ? (
        <Alert severity="info">Your permission level cannot change protected Purchasing workflow settings. Please contact a Super Administrator for this instance to request a change. Other Purchasing preferences remain editable.</Alert>
      ) : null}
      {!canManage ? (
        <Alert severity="info">
          You can view Purchasing settings, but only managers and administrators can change them.
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Purchase Orders
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Control which extra Purchasing references appear on the Purchase Orders / Proofs page.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={purchasingSettings.showPoUrl === true}
                  disabled={!canManage || saving || loading}
                  onChange={(event) =>
                    savePurchasingSettings({ showPoUrl: event.target.checked })
                  }
                />
              }
              label="Show URL under PO#"
            />
            <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
              Displays a URL link beneath the PO number on the Purchase Orders / Proofs page.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={purchasingSettings.showPoNotesAction === true}
                  disabled={!canManage || saving || loading}
                  onChange={(event) =>
                    savePurchasingSettings({
                      showPoNotesAction: event.target.checked,
                    })
                  }
                />
              }
              label="Show Notes action on PO rows"
            />
            <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
              Displays the Notes button in each Purchase Order row on the Purchase Orders / Proofs page.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={purchasingSettings.infiniteDashboardScroll === true}
                  disabled={!canManage || saving || loading}
                  onChange={(event) =>
                    savePurchasingSettings({
                      infiniteDashboardScroll: event.target.checked,
                    })
                  }
                />
              }
              label="Enable infinite scroll on Purchasing Dashboard"
            />
            <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
              Loads additional purchase orders automatically on the Purchasing Dashboard instead of using page navigation.
            </Typography>
            {canManageFoundation ? (<>
            <FormControlLabel
              control={
                <Checkbox
                  checked={purchasingSettings.requireShippingConfirmation === true}
                  disabled={!canManage || !canManageFoundation || saving || loading}
                  onChange={(event) =>
                    savePurchasingSettings({
                      requireShippingConfirmation: event.target.checked,
                    })
                  }
                />
              }
              label="Require shipping confirmation before Purchase Orders"
            />
            <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
              When enabled, each shipping record must be manually confirmed before Purchase Orders can proceed. When disabled, Elite Core still requires complete shipping information but skips the additional confirmation step.
            </Typography>
            </>) : null}
          </Stack>
        </CardContent>
      </Card>

      {canManageFoundation ? (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Proof Requirements Before PO Generation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose how Elite Core should validate proofs before Purchase Orders can be generated.
            </Typography>
            <FormControl disabled={!canManage || !canManageFoundation || saving || loading}>
              <RadioGroup
                aria-label="Proof enforcement mode"
                name="proofEnforcementMode"
                value={purchasingSettings.proofEnforcementMode}
                onChange={(event) =>
                  savePurchasingSettings({ proofEnforcementMode: event.target.value })
                }
              >
                {PROOF_ENFORCEMENT_OPTIONS.map((option) => (
                  <Box key={option.value}>
                    <FormControlLabel
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 4 }}>
                      {option.description}
                    </Typography>
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>

            <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={purchasingSettings.evaluateProofRequirementsPerPurchaseOrder === true}
                    disabled={!canManage || saving || loading}
                    onChange={(event) =>
                      savePurchasingSettings({
                        evaluateProofRequirementsPerPurchaseOrder: event.target.checked,
                      })
                    }
                  />
                }
                label="Evaluate proof requirements independently per Purchase Order"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 4 }}>
                When enabled, eligible Purchase Orders can generate while other PO candidates remain waiting on proof. You can return later and generate newly eligible remaining POs. Existing generated POs are preserved.
              </Typography>
              {purchasingSettings.proofEnforcementMode === PROOF_ENFORCEMENT_MODES.NONE ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  The per-PO option has no proof-blocking effect while “No proof check before generation” is selected, but incremental PO generation remains available.
                </Alert>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>


      ) : null}      {loading ? <Alert severity="info">Loading Purchasing settings...</Alert> : null}
    </Box>
  );
};

export default PurchasingSettingsPanel;
